import { cloneDeep, get, set, uniqBy } from 'lodash';
import { APIS } from 'src/lib/constants/apis';
import {
	ANTRAG_VERBUNDPARTNER,
	FOERDERPROGRAMM_EINREICHENFRIST_PATHS,
	FOERDERPROGRAMM_FOERDERQUOTE_ANTRAGSBERECHTIGTE_PATHS,
} from 'src/lib/constants/componentsPath';
import { ANTRAGS_FORMULAREN, ANTRAGS_STATUS, MONAT_LISTE, PRUEF_ASSISTENZ_STATUS } from 'src/lib/constants/constant';
import { GetCompareMinMaxEqualDate, GetDateGTEMaxYearValidation, Required, ValidDate } from 'src/lib/validation/ValidationTypes';
import {
	addIndexToPath,
	enumT,
	getFieldNamefromDataPath,
	getIDFromResponseOption,
	getOptionsFromRes,
	isAntragBerechtigtHochschule,
	isDateInRange,
	isObjectEmpty,
	isOrgaAbteilungEinerHochschule,
	isTrue,
	parseDatum,
} from 'src/utils/helper';

// import schemas
import AntragSchema from 'src/lib/paths/antrag_FULL_Paths.json';
// import AntragsberechtigteSchema from 'src/lib/paths/antragsberechtigte_FULL_Paths.json';
import CallsSchema from 'src/lib/paths/callforproposal_FULL_Paths.json';
import FoerderProgrammSchema from 'src/lib/paths/foerderprogramm_FULL_Paths.json';
import KostenKategorieSchema from 'src/lib/paths/kostenkategorie_FULL_Paths.json';
import KostenPositionSchema from 'src/lib/paths/kostenposition_FULL_Paths.json';
import LeistungsGruppeSchema from 'src/lib/paths/leistungsgruppe_FULL_Paths.json';
import MittelabrufSchema from 'src/lib/paths/mittelabruf_FULL_Paths.json';
import NachweisPositionSchema from 'src/lib/paths/nachweisposition_FULL_Paths.json';
import OrgaSchema from 'src/lib/paths/organisation_FULL_Paths.json';
const P = AntragSchema.attributes;
const MA_P = MittelabrufSchema.attributes;
const KP_P = KostenPositionSchema.attributes;
const NP_P = NachweisPositionSchema.attributes;
// const AB_P = AntragsberechtigteSchema.attributes;
const FP_P = FoerderProgrammSchema.attributes;
const KK_P = KostenKategorieSchema.attributes;
const CP_P = CallsSchema.attributes;
const ORGA_P = OrgaSchema.attributes;
const LG_P = LeistungsGruppeSchema.attributes;

const DEFAULT_FRIST = {
	vonTag: 1,
	vonMonat: MONAT_LISTE[0].Monat,
	vonJahr: null,
	bisTag: 31,
	bisMonat: MONAT_LISTE[11].Monat,
	bisJahr: null,
};

export const mergeFoerderProgramm = (callProposalData: any, foerderprogrammData: any) => {
	let newFoerderprogramm = cloneDeep(foerderprogrammData);

	if (!isObjectEmpty(callProposalData)) {
		const antragFristenMapping = {
			// Antragfristen
			[FP_P.Ab_Wann_Einreichung_Moeglich.path]: CP_P.Antragsfrist.schema.attributes.Ab_Wann_Einreichung_Moeglich.path,
			[FP_P.Bis_Wann_Einreichung_Moeglich.path]: CP_P.Antragsfrist.schema.attributes.Bis_Wann_Einreichung_Moeglich.path,
			[FP_P.Ab_Wann_Ist_Ein_Fruehester_Projektstart_Moeglich.path]:
				CP_P.Antragsfrist.schema.attributes.Ab_Wann_Ist_Ein_Fruehester_Projektstart_Moeglich.path,
			[FP_P.Bis_Wann_Muss_Das_Projekt_Beendet_Sein.path]: CP_P.Antragsfrist.schema.attributes.Bis_Wann_Muss_Das_Projekt_Beendet_Sein.path,
		};
		for (const [key, value] of Object.entries(antragFristenMapping)) {
			const newValue = get(callProposalData, value, null);
			if (value) {
				set(newFoerderprogramm, `${key}`, newValue);
			}
		}

		const finanzierungMapping = {
			// Finanzierung
			[FP_P.Finanzierung_Zuwendungsart.path]: CP_P.Finanzierung.schema.attributes.Zuwendungsart.path,
			[FP_P.Foerderquote_Antragsberechtigte.path]: CP_P.Finanzierung.schema.attributes.Foerderquote.path,
		};
		for (const [key, value] of Object.entries(finanzierungMapping)) {
			const newValue = get(callProposalData, value, null);
			if (value) {
				set(newFoerderprogramm, `${key}`, newValue);
			}
		}
	}

	return newFoerderprogramm;
};

export const checkEinreichenMoeglich = (api: string, application: any, submit: boolean) => {
	// Wenn submit false, dann soll die Einrechenmöghlichkeit nicht geprüft werden
	// Egal welche Antrag
	if (!isTrue(submit)) return true;

	if (api === APIS.Antrag) {
		const antragStatus = get(application, P.Antragstatus_Antragstatus.path);
		if (antragStatus === ANTRAGS_STATUS.inBearbeitung) {
			// CUSTOM Check ist erforderliche, weil Antrag (Laut jetzige AFO) ein genaues Datum mit Jahr (z.B. 31.20.2025) hat
			return checkCustomAntragEinreichenFirst(application);
		} else {
			return true;
		}
	}

	return isDateInRange(getEinreichenFirst(api, application));
};

function checkCustomAntragEinreichenFirst(application: any) {
	const currentDate = new Date();
	const currentYear = currentDate.getFullYear();

	const foerderprogram = get(application, P.Foerderprogramm.path, {});

	// Daten aus der Datenbank
	const abWann_DB = get(foerderprogram, FP_P.Ab_Wann_Einreichung_Moeglich.path);
	const bisWann_DB = get(foerderprogram, FP_P.Bis_Wann_Einreichung_Moeglich.path);

	// Fallbacks verwenden
	// Wenn Datum nicht in der Datenbank gesetzt ist, dann ist immer mäglich
	// Daher ab und bis sind 01.01.(aktuelles Jahr) und 31.12.(aktuelles Jahr)
	const abWannEinreichungMoeglich = new Date(abWann_DB || `${currentYear}-01-01`);
	abWannEinreichungMoeglich.setHours(0, 0, 1, 0);
	const bisWannEinreichungMoeglich = new Date(bisWann_DB || `${currentYear}-12-31`);
	bisWannEinreichungMoeglich.setHours(23, 59, 59, 999);

	// Vergleich: heute liegt zwischen abWann und bisWann
	return currentDate >= abWannEinreichungMoeglich && currentDate <= bisWannEinreichungMoeglich;
}

function getEinreichenFirst(api: string, application: any) {
	if (api === APIS.Antrag) {
		const antragStatus = get(application, P.Antragstatus_Antragstatus.path);

		if (antragStatus === ANTRAGS_STATUS.inBearbeitung) {
			const foerderprogram = get(application, P.Foerderprogramm.path, {});

			const abWann = get(foerderprogram, FP_P.Ab_Wann_Einreichung_Moeglich.path);
			const bisWann = get(foerderprogram, FP_P.Bis_Wann_Einreichung_Moeglich.path);

			// Jahr direkt aus dem Datum extrahieren
			const vonJahr = abWann ? new Date(abWann).getFullYear() : new Date().getFullYear();
			const bisJahr = bisWann ? new Date(bisWann).getFullYear() : new Date().getFullYear();

			const { tag: vonTag, monat: vonMonat } = parseDatum(abWann, 1, 0); // 1. Januar
			const { tag: bisTag, monat: bisMonat } = parseDatum(bisWann, 31, 11); // 31. Dezember

			const result = {
				vonTag,
				vonMonat,
				vonJahr,
				bisTag,
				bisMonat,
				bisJahr,
			};

			return result;
		} else {
			return DEFAULT_FRIST;
		}
	} else if (api === APIS.Mittelabruf) {
		const mittelAbrufStatus = get(application, MA_P.Mittelabrufstatus_Mittelabrufstatus.path);

		if (mittelAbrufStatus === ANTRAGS_STATUS.inBearbeitung) {
			const foerderprogram = get(application, MA_P.Foerderantrag.schema.attributes.Foerderprogramm.path, {});
			const einreichenFristen = get(foerderprogram, FP_P.Einreichfristen.path, {});

			return {
				vonTag: get(einreichenFristen, FOERDERPROGRAMM_EINREICHENFRIST_PATHS.mitttelabrufVonTag, 1),
				vonMonat: get(einreichenFristen, FOERDERPROGRAMM_EINREICHENFRIST_PATHS.mitttelabrufVonMonat, MONAT_LISTE[0].Monat),
				vonJahr: null,
				bisTag: get(einreichenFristen, FOERDERPROGRAMM_EINREICHENFRIST_PATHS.mitttelabrufBisTag, 31),
				bisMonat: get(einreichenFristen, FOERDERPROGRAMM_EINREICHENFRIST_PATHS.mitttelabrufBisMonat, MONAT_LISTE[11].Monat),
				bisJahr: null,
			};
		} else {
			return DEFAULT_FRIST;
		}
	} else if (api === APIS.Verwendungsnachweis) {
		return DEFAULT_FRIST;
	} else if (api === APIS.Zwischenbericht) {
		return DEFAULT_FRIST;
	} else if (api === APIS.Abschlussbericht) {
		return DEFAULT_FRIST;
	}
	return DEFAULT_FRIST;
}

export function getEinreichenFirstDisplayDialog(api: string, application: any) {
	const einreichenFristen = getEinreichenFirst(api, application);

	// Prüfen ob Jahr vorhanden ist (nur bei Antrag)
	const vonDatum =
		einreichenFristen.vonJahr ?
			`${einreichenFristen.vonTag.toString().padStart(2, '0')}. ${enumT(einreichenFristen.vonMonat)} ${einreichenFristen.vonJahr}`
		:	`${einreichenFristen.vonTag.toString().padStart(2, '0')}. ${enumT(einreichenFristen.vonMonat)}`;

	const bisDatum =
		einreichenFristen.bisJahr ?
			`${einreichenFristen.bisTag.toString().padStart(2, '0')}. ${enumT(einreichenFristen.bisMonat)} ${einreichenFristen.bisJahr}`
		:	`${einreichenFristen.bisTag.toString().padStart(2, '0')}. ${enumT(einreichenFristen.bisMonat)}`;

	return `Eine Einreichung ist aktuell nicht möglich.\n\n Anträge können nur im Zeitraum **vom ${vonDatum} bis ${bisDatum}** eingereicht werden. \n\n Außerhalb dieses Zeitraums ist eine Einreichung nicht möglich. \n\n Für weitere Informationen wenden Sie sich bitte an das Team der Innovationsförderung Hessen.`;
}

export const checkIfGroupsHaveNegativeValues = (groups: any, propertiesToCheck: string[]) => {
	for (const group in groups) {
		const elem = groups[group];
		for (const key in elem) {
			if (propertiesToCheck.includes(key)) {
				const value = elem[key];
				if (Array.isArray(value)) {
					if (value.some((item) => item < 0)) {
						return true;
					}
				} else if (typeof value === 'number' && value < 0) {
					return true;
				} else if (typeof value === 'string' && !isNaN(parseFloat(value)) && parseFloat(value) < 0) {
					return true;
				}
			}
		}
	}
	return false;
};

export const findYearsWithMissingEinnahme = (totals: any, allyears: number[]): number[] => {
	const result: number[] = [];
	const { gesamtausgaben, summeEinnahme } = totals;
	for (let i = 0; i < allyears.length; i++) {
		if (gesamtausgaben[i] > 0 && summeEinnahme[i] === 0) {
			result.push(allyears[i]);
		}
	}
	return result;
};

export const checkIfTotalsHaveNegativeValues = (totals: any, propertiesToCheck: string[]) => {
	for (const key in totals) {
		if (propertiesToCheck.includes(key)) {
			const value = totals[key];
			if (Array.isArray(value)) {
				if (value.some((item) => item < 0)) {
					return true;
				}
			} else if (typeof value === 'number' && value < 0) {
				return true;
			} else if (typeof value === 'string' && !isNaN(parseFloat(value)) && parseFloat(value) < 0) {
				return true;
			}
		}
	}
	return false;
};

export const checkIfPruefAssitenzNeedsToValidate = (ObjectToVerify: any, statusPath: string, isErledigtPath: string) => {
	if (get(ObjectToVerify, statusPath) === PRUEF_ASSISTENZ_STATUS.uberarbeitung && !isTrue(get(ObjectToVerify, isErledigtPath))) {
		return true;
	}
	return false;
};

export const getPruefAssistenzInfoType = (application: any, isErledigtPath: string) => {
	if (isTrue(get(application, isErledigtPath))) return 'success';
	else return 'warning';
};

// export const hasNodeComment = (readOnly: boolean, application: any, statusPath: string | any[], hinweisPath: string | any[]) => {
// 	let hasComment = false;
// 	if (readOnly) return hasComment;

// 	if (typeof statusPath === 'string' && typeof hinweisPath === 'string') {
// 		if (get(application, statusPath, '') === PRUEF_ASSISTENZ_STATUS.uberarbeitung || get(application, hinweisPath, '')) {
// 			hasComment = true;
// 		}
// 	}

// 	if (isArray(statusPath) && isArray(hinweisPath)) {
// 		for (let [index, eachStatusPath] of statusPath) {
// 			const eachHinweisPath = hinweisPath[index];
// 			if (get(application, eachStatusPath, '') === PRUEF_ASSISTENZ_STATUS.uberarbeitung || get(application, eachHinweisPath, '')) {
// 				hasComment = true;
// 				return true;
// 			}
// 		}
// 	}

// 	return hasComment;
// };

export const hasAnyNachweisPositionComment = (
	readOnly: boolean,
	application: any /*Hier die Mittelabruf als Objekt gegeben*/,
	nachweisPositionPath: string,
	filterCriteria: 'KostenPositionId' | 'KostenArt', // Nur eine von den beiden string ist erlaubt
	kostenPostionId?: number,
	pathToFiler?: string,
	valueToFilter?: any,
) => {
	if (readOnly) return false;

	let hasComment = false;
	const allNachweisPosition = get(application, nachweisPositionPath, []);
	let filteredNachweisPosition: any[] = [];

	if (filterCriteria === 'KostenPositionId') {
		filteredNachweisPosition = allNachweisPosition.filter(
			(eachNachweisPosition: any) => get(eachNachweisPosition, `${NP_P.Allgemein_Kostenposition.path}.id`, 0) === kostenPostionId,
		);
	} else if (filterCriteria === 'KostenArt') {
		filteredNachweisPosition = allNachweisPosition.filter(
			(eachNachweisPosition: any) => get(eachNachweisPosition, pathToFiler, 0) === valueToFilter,
		);
	}

	for (const eachFilteredNachweisPosition of filteredNachweisPosition) {
		if (get(eachFilteredNachweisPosition, NP_P.Pruefung_Hinweis_Fuer_Den_Antragsteller.path)) {
			hasComment = true;
			return true;
		}
	}

	return hasComment;
};

export const getKostenErlauterung = (allKostenKategories: any, kategorieId: any, antragsformular?: string) => {
	let selectedKategorie: any = {};

	if (kategorieId) {
		if (typeof kategorieId === 'object') {
			selectedKategorie = allKostenKategories.filter((ele: any) => ele.id === kategorieId.id);
		} else {
			selectedKategorie = allKostenKategories.filter((ele: any) => ele.id === kategorieId);
		}
	} else {
		if (antragsformular) {
			switch (antragsformular) {
				case ANTRAGS_FORMULAREN.StartUpStipendium:
					return 'Bitte wählen Sie im ersten Schritt die Beschäftigungsart gemäß Personalkostenkalkulation aus und geben Sie anschließend die Personalkosten pro Projektjahr an.';
				default:
					return 'Bitte wählen Sie im ersten Schritt die Kategorie aus und geben dann eine genauere Bezeichnung der Kosten an.';
			}
		}
		return 'Bitte wählen Sie im ersten Schritt die Kategorie aus und geben Sie anschließend den Betrag ein.';
	}

	return get(selectedKategorie[0], 'attributes.Allgemein_Erlaeuterung_Fuer_Den_Antragsteller', '');
};

export const getBasicLeistungsGruppeFilter = (application: any, antragsFormular?: string) => {
	const currentDate = new Date().toISOString();

	const antragsformular = antragsFormular || get(application, P.Allgemein_Antragsformular.path);

	return {
		[getFieldNamefromDataPath(LG_P.Allgemein_Antragsformular.path)]: { $contains: antragsformular },
		[getFieldNamefromDataPath(LG_P.Allgemein_Aktiv.path)]: { $eq: true },
		[getFieldNamefromDataPath(LG_P.Allgemein_Gueltig_Ab.path)]: { $lte: currentDate },
		[getFieldNamefromDataPath(LG_P.Allgemein_Gueltig_Bis.path)]: { $gte: currentDate },
	};
};

export const getBasisDataKostenKategorie = (
	application: any,
	allKostenkategorie: any[],
	kostentraeger: any,
	unternehmenId: any,
	kostenposition: any,
	kostenArt: any,
) => {
	const defaultKostenkategorieId = getInitialKostenKategorieID(allKostenkategorie);

	/*
	const data = getBasisDataKostenKategorie(
		appCopy,
		kostenKategorie, // All kostenkategorie for Sachkosten, to find default Kostenkategorie id
		kostentraeger,
		unternehmenId,
		KOSTEN_POSITION.Ausgabe,
		KOSTEN_ARTEN.Sachkosten,
	);
	 */

	const data = {
		Allgemein_Kostenkategorie: defaultKostenkategorieId,
		Foerderantrag: get(application, 'id'),
		Allgemein_Kostentraeger: kostentraeger,
		Unternehmen: unternehmenId,
		Allgemein_Kostenposition: kostenposition,
		Allgemein_Kostenart_Finanzierung: kostenArt,
		Allgemein_Kosten_Pro_Jahr: Array.from({
			length: get(application, P.Allgemein_Projektdauerin_Jahren.path, 0),
		}).map((_, i) => {
			return {
				Zelle_1: parseInt(get(application, P.Allgemein_Projektstart_Jahr.path)) + i,
			};
		}),
	};

	return data;
};

//  Ermittelt die initiale Kostenkategorie-ID
export const getInitialKostenKategorieID = (allKategorie: any[], minOneRequired: boolean = false) => {
	let id = null;

	// Suche nach einer Kategorie, die explizit als initial ausgewählt markiert ist
	const initialKategorie = allKategorie.find((ele: any) => {
		return isTrue(get(ele, KK_P.Zusaetzliche_Informationen_Initial_Ausgewaehlt.path, false));
	});

	// Wenn eine initial markierte Kategorie gefunden wurde UND nicht mindestens eine Required ist
	// -> Verwende die gefundene Kategorie
	if (!isObjectEmpty(initialKategorie)) {
		id = get(initialKategorie, 'id', null);
	}
	// Falls mindestens eine Kategorie erforderlich ist
	// -> Verwende die erste verfügbare Kategorie als Fallback
	else if (minOneRequired) {
		id = get(allKategorie[0], 'id', null);
	}
	// Sonst bleibt id = null

	return id;
};

/**
 * selectedKategorie kann ein ID sein, wenn im Portal gerade die Kategorie ausgewählt ist
 * ABER wenn die Kategorie schon vorhanden ist, dann wird im Feld eine Object vorhanden.
 * So ist immer bei jedem Auswahlliste, wo Verknüpfungen auszuwählen ist
 * @param selectedKategorie | Object oder ID
 * @param kostenPositionObj | Object
 * @returns | Der wert in das Feld Bezeichung von Kostenkategorie
 */
export const getSelectedKostenKategorieBezeichnung = (allKostenKategoriesARR: any[], selectedKategorie: any) => {
	let kategorie: any = {};

	if (selectedKategorie) {
		if (typeof selectedKategorie === 'object') {
			kategorie = allKostenKategoriesARR.filter((ele: any) => ele.id === selectedKategorie.id);
		} else {
			kategorie = allKostenKategoriesARR.filter((ele: any) => ele.id === selectedKategorie);
		}
	}

	return get(kategorie[0], KK_P.Allgemein_Bezeichnung.path, '');
};

export const getSelectedKostenKategorieObject = (allKostenKategoriesARR: any[], selectedKategorie: any) => {
	let kategorie: any = {};

	if (selectedKategorie) {
		if (typeof selectedKategorie === 'object') {
			kategorie = allKostenKategoriesARR.find((ele: any) => ele.id === selectedKategorie.id);
		} else {
			kategorie = allKostenKategoriesARR.find((ele: any) => ele.id === selectedKategorie);
		}
	}

	return kategorie;
};

export const getSelectedInstitutionObject = (allInstitutionOptionsARR: any[], selectedInstitution: any) => {
	let institution: any = {};
	if (selectedInstitution) {
		const antragsberechtigtePath = getFieldNamefromDataPath(
			P.Foerderprogramm.schema.attributes.Foerderquote_Antragsberechtigte.schema.attributes.Antragsberechtigte.path,
		);
		if (typeof selectedInstitution === 'object') {
			institution = allInstitutionOptionsARR?.find((ele: any) => get(ele, antragsberechtigtePath + '.id') === selectedInstitution.id);
			institution = institution ? get(institution, antragsberechtigtePath) : {};
		} else {
			institution = allInstitutionOptionsARR?.find((ele: any) => get(ele, antragsberechtigtePath + '.id') === selectedInstitution);
			institution = institution ? get(institution, antragsberechtigtePath) : {};
		}
	}

	return institution;
};

export const getIsAntragsberechtigAlsHochschule = (application: any, index: number, institutionsOptionsArr: any) => {
	const antragsberechtigtAls = get(
		application,
		addIndexToPath(P.Verbundpartner.schema.attributes.Antragsberechtigt_Als.path, index, P.Verbundpartner.path),
		null,
	);
	const antragsberechtigtAlsMapped = getSelectedInstitutionObject(institutionsOptionsArr, antragsberechtigtAls);
	const isAntragsberechtigAlsHochschule = isAntragBerechtigtHochschule(antragsberechtigtAlsMapped);

	return isAntragsberechtigAlsHochschule;
};
// Kürzel anzeigen wenn eine Hochschule, sonst Name
export const getVerbundPartnerName = (verbundpartner: any, index: number) => {
	const vPartnerName =
		get(verbundpartner, ANTRAG_VERBUNDPARTNER.Name) ? get(verbundpartner, ANTRAG_VERBUNDPARTNER.Name) : `Verbundpartner ${index + 1}`;
	const vPartnerKuerzel =
		get(verbundpartner, ANTRAG_VERBUNDPARTNER.Kuerzel) ? get(verbundpartner, ANTRAG_VERBUNDPARTNER.Kuerzel) : `Verbundpartner ${index + 1}`;
	const isAntragsberechtigt = isAntragBerechtigtHochschule(get(verbundpartner, ANTRAG_VERBUNDPARTNER.Antragberechtigt));

	return isAntragsberechtigt ? vPartnerKuerzel : vPartnerName;
};

export const getAntragstellerName = (application: any, checkHochschuleName?: boolean) => {
	const antragstellerOrga = get(application, P.Antragstellende_Organisation.path);
	let name = '';
	if (isOrgaAbteilungEinerHochschule(antragstellerOrga)) {
		const hochschulname = `${get(antragstellerOrga, ORGA_P.Hochschule.schema.attributes.Geschaeftsbezogene_Daten_Name_Der_Organisation.path, '')} (FB: ${get(antragstellerOrga, ORGA_P.Geschaeftsbezogene_Daten_Name_Der_Organisation.path, '')})`;
		name = checkHochschuleName ? hochschulname : get(antragstellerOrga, ORGA_P.Geschaeftsbezogene_Daten_Kuerzel.path, '');
	}
	return name ? name : get(application, P.Antragstellende_Organisa_0074063902_licher_Name_Der_Organisation.path, '');
};

export const getKostenKategorieMittelherkunftBenoetigt = (selectedKostenKategorie: any, kostenKategorie: any[]): boolean | null => {
	if (!selectedKostenKategorie) return null;

	if (typeof selectedKostenKategorie === 'object' && selectedKostenKategorie !== null) {
		return get(selectedKostenKategorie, KK_P.Zusaetzlicheinfo_Erlauterung_Zur_Mittelherkunft_Notwendig.path, null);
	}

	if (typeof selectedKostenKategorie === 'number') {
		const found = kostenKategorie.find((kategorie: any) => kategorie.id === selectedKostenKategorie);
		return found ? get(found, KK_P.Zusaetzlicheinfo_Erlauterung_Zur_Mittelherkunft_Notwendig.path, null) : null;
	}

	return null;
};

// Anzeigename der VP mit Hochschule als Klarname, nicht kürzel
export const getVPAnzeigeName = (verbundpartner: any) => {
	const vPartnerName = get(verbundpartner, getFieldNamefromDataPath(ANTRAG_VERBUNDPARTNER.Name));
	const hochschule = get(verbundpartner, getFieldNamefromDataPath(ANTRAG_VERBUNDPARTNER.Hochschule));
	const vPartnerHochschuleName = get(hochschule, 'attributes.' + getFieldNamefromDataPath(ANTRAG_VERBUNDPARTNER.HochschuleName));
	const isNeueHochschule = isTrue(get(verbundpartner, getFieldNamefromDataPath(P.Verbundpartner.schema.attributes.Neue_Hochschule.path), false));
	const vPartnerFachbereich = get(verbundpartner, getFieldNamefromDataPath(P.Verbundpartner.schema.attributes.Fachbereich.path));
	const vPartnerFBName = isNeueHochschule ? vPartnerFachbereich : vPartnerName;
	const vPartnerHochschulNameNeu = isNeueHochschule ? vPartnerName : vPartnerHochschuleName;
	const isAntragsberechtigt = isAntragBerechtigtHochschule(get(verbundpartner, getFieldNamefromDataPath(ANTRAG_VERBUNDPARTNER.Antragberechtigt)));
	const vPartnerHochschulFBName =
		vPartnerHochschulNameNeu && vPartnerFBName ? `${vPartnerHochschulNameNeu} (FB: ${vPartnerFBName})` : vPartnerHochschulNameNeu;
	return (
		isAntragsberechtigt ?
			vPartnerHochschulFBName ? vPartnerHochschulFBName
			:	vPartnerName
		:	vPartnerName
	);
};

export const getZielFoerderQuote = (application: any, antragberechtigte: number) => {
	const foererProgram = get(application, P.Foerderprogramm.path, {});
	const antragberechtigteID = getIDFromResponseOption(antragberechtigte);

	const foerderQuoteBlock_ARR = get(foererProgram, FP_P.Foerderquote_Antragsberechtigte.path, []);
	const foerderQuote_Obj = foerderQuoteBlock_ARR.find(
		(ele: any) => get(ele, FOERDERPROGRAMM_FOERDERQUOTE_ANTRAGSBERECHTIGTE_PATHS.AntragberechtigtID) === antragberechtigteID,
	);
	return get(foerderQuote_Obj, 'Foerderquote', 0);
};

export const getInstitutionsTypOption = (application: any) => {
	let result: any[] = [];

	const foerderprogram = get(application, P.Foerderprogramm.path, {});
	const foerderquoteAntragsberechtigteBlock_ARR = get(foerderprogram, FP_P.Foerderquote_Antragsberechtigte.path, []);

	const option = uniqBy(
		(foerderquoteAntragsberechtigteBlock_ARR || []).map((ele: any) =>
			get(ele, FOERDERPROGRAMM_FOERDERQUOTE_ANTRAGSBERECHTIGTE_PATHS.Antragsberechtigte, {}),
		),
		'id',
	);
	if (typeof option === 'string') {
		result.push(option);
	} else {
		result = getOptionsFromRes(option, 'Allgemein_Bezeichnung', false);
	}
	return result;
};

export const getProjektLaufzeitVON_Validation = (application: any) => {
	const foerderprogram = get(application, P.Foerderprogramm.path, {});

	const fruehesterProjektstart = get(foerderprogram, FP_P.Ab_Wann_Ist_Ein_Fruehester_Projektstart_Moeglich.path, null);
	const createdAt = new Date(get(application, 'attributes.createdAt'));

	// Nimm das spätere Datum (Maximum) von beiden
	const minDate = new Date(fruehesterProjektstart) > new Date(createdAt) ? fruehesterProjektstart : createdAt;

	return {
		validation: [
			{ validateFunction: Required },
			{ validateFunction: ValidDate },
			{
				validateFunction: GetDateGTEMaxYearValidation,
				params: [minDate, 20],
			},
		],
	};
};

export const getProjektLaufzeitBIS_Validation = (application: any) => {
	const foerderprogram = get(application, P.Foerderprogramm.path, {});

	const date = new Date();
	date.setFullYear(date.getFullYear() + 10);

	return {
		validation: [
			{ validateFunction: Required },
			{ validateFunction: ValidDate },
			{
				validateFunction: GetCompareMinMaxEqualDate,
				params: [
					get(application, P.Allgemein_Geplante_Projektlaufzeit_von.path),
					get(foerderprogram, FP_P.Bis_Wann_Muss_Das_Projekt_Beendet_Sein.path, date),
				],
			},
		],
	};
};
// ***********************************************************************************************************************
// Wenn Projektlaufzeit gesetzt ist, dann muss Projektlaufzeit BIS kleiner oder gleich Von-Datum plus Projektlaufzeit sein
// Ansonsten nimmt das Datum aus der Datenbank (Bis_Wann_Muss_Das_Projekt_Beendet_Sein) als Maximum, wenn es vorhanden ist
// ----- Erstmal nur in Stipendium genutzt. Wenn gewünscht, kann es die getProjektLaufzeitBIS_Validation ersetzen --------

export const getProjektLaufzeitBISMitProjektlaufzeit_Validation = (application: any) => {
	const foerderprogram = get(application, P.Foerderprogramm.path, {});
	const projektLaufzeitInMonaten = get(foerderprogram, FP_P.Antragsfrist_Maximale_Projektlaufzeitin_Monaten.path, null);
	const fallbackDate = new Date();
	fallbackDate.setFullYear(fallbackDate.getFullYear() + 10);

	const projektLaufzeitVon = get(application, P.Allgemein_Geplante_Projektlaufzeit_von.path);
	const projektEndDatumDB = get(foerderprogram, FP_P.Bis_Wann_Muss_Das_Projekt_Beendet_Sein.path, null);

	// Von-Datum plus max-projektlaufzeit minus einen Tag
	let vonDate = projektLaufzeitVon ? new Date(projektLaufzeitVon) : null;
	let vonDatePlusProjektlaufzeit = null;
	if (vonDate && !isNaN(vonDate.getTime()) && projektLaufzeitInMonaten > 0) {
		vonDatePlusProjektlaufzeit = new Date(vonDate);
		vonDatePlusProjektlaufzeit.setMonth(vonDatePlusProjektlaufzeit.getMonth() + projektLaufzeitInMonaten);
		vonDatePlusProjektlaufzeit.setDate(vonDatePlusProjektlaufzeit.getDate() - 1);
	}
	let projektEndDatum = fallbackDate;
	if (projektEndDatumDB && vonDatePlusProjektlaufzeit) {
		const dbDate = new Date(projektEndDatumDB);
		// nimm das frühere Datum
		projektEndDatum = dbDate < vonDatePlusProjektlaufzeit ? dbDate : vonDatePlusProjektlaufzeit;
	} else if (projektEndDatumDB) {
		projektEndDatum = new Date(projektEndDatumDB);
	}
	return {
		validation: [
			{ validateFunction: Required },
			{ validateFunction: ValidDate },
			{
				validateFunction: GetCompareMinMaxEqualDate,
				params: [projektLaufzeitVon, projektEndDatum],
			},
		],
	};
};

export const getStandortNameMitGruopID = (applciation: any, standortGroupID: string) => {
	const standort_Arr = get(
		applciation,
		P.Projektbeschreibung_Elektromobilitaet_LIS.schema.attributes.Standort_Spezifikation_Ladeinfrastruktur.path,
		[],
	);

	const standOrt = standort_Arr.find((ele: any) => get(ele, 'Group_45_ID', null) === standortGroupID);
	return get(standOrt, 'Strasse_Und_Hausnummer', '');
};

/**
 * Berechnet die jährlichen Summen für eine bestimmte Kostenposition.
 */
export const getYearlySums = (kostenpositionen: any[], kostenpositionType: string) => {
	return kostenpositionen.reduce((acc: Record<number, number>, ele: any) => {
		if (get(ele, KP_P.Allgemein_Kostenposition.path, '') !== kostenpositionType) return acc;

		const kostenProJahr = get(ele, KP_P.Allgemein_Kosten_Pro_Jahr.path, []);
		for (const eachYear of kostenProJahr) {
			const year = Number(get(eachYear, 'Zelle_1'));
			if (Number.isNaN(year)) continue;

			const value = parseFloat(get(eachYear, 'Zelle_2', '0.0')) || 0;
			acc[year] = (acc[year] || 0) + value;
		}
		return acc;
	}, {});
};
